import { CaretDown, CaretRight, Check } from '@phosphor-icons/react';
import styled from 'styled-components';
import { Input, TableCell } from 'semantic-ui-react';
import { useMemo } from 'react';
import { BoldText, ColoredPill, MuteText } from '../../../elements';
import { NumberMaskInput } from '../../../elements/CustomMaskInput';
import { ItemImage } from '../../../components';
import { ProductPropertiesConverted } from '../common';
import { theme } from '../../../utility';

interface IExpandCollapseToggleProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  title?: string;
  className?: string;
  expandText?: string;
  collapseText?: string;
}

const ExpandCollapseContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  cursor: pointer;
  .icon {
    min-width: 16px;
  }

  .text {
    word-break: keep-all;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.grey};
  }
`;

export function ExpandCollapseToggle({
  open,
  setOpen,
  title = 'Multiple Aliases',
  className = '',
  expandText = 'Expand',
  collapseText = 'Collapse',
}: IExpandCollapseToggleProps) {
  return (
    <ExpandCollapseContainer className={className}>
      {title && <MuteText>{title}</MuteText>}
      <div
        onClick={() => setOpen(!open)}
        className="d-flex align-items-center cursor-pointer"
      >
        {open ? (
          <>
            <span className="text">{collapseText}</span>
            <CaretDown className="ml-1 icon" />
          </>
        ) : (
          <>
            <span className="text">{expandText}</span>
            <CaretRight className="ml-1 icon" />
          </>
        )}
      </div>
    </ExpandCollapseContainer>
  );
}

const MiniInput = styled(Input)`
  width: 70px;
`;

interface IItemMiniInputProps {
  value: number;
  onChange: (value: string) => void;
  maxQty: number;
  noMax?: boolean;
  maskOptions?: Record<string, any>;
  disabled?: boolean;
  placeholder?: string;
}

export function ItemMiniInput({
  value,
  onChange,
  maxQty,
  noMax = false,
  maskOptions = {},
  disabled = false,
  placeholder = '',
}: IItemMiniInputProps) {
  const maxValue = Math.floor(maxQty);

  return (
    <MiniInput size="mini">
      <NumberMaskInput
        value={value && Math.round(value)}
        onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter') e.preventDefault();
        }}
        onChange={onChange}
        maxValue={noMax ? undefined : maxValue < 0 ? 0 : maxValue}
        maskOptions={maskOptions}
        disabled={disabled}
        placeholder={placeholder}
      />
    </MiniInput>
  );
}

interface IReadOnlyItemDetailsProps {
  item: {
    p_title: string;
    p_sku: string;
    item_upc: string;
    alias?: string;
    aliases?: { alias: string }[];
    [key: string]: any;
  };
  open: boolean;
  setOpen: (open: boolean) => void;
  latestAttribute: Record<string, any>;
  imperial: boolean;
}

export function ReadOnlyItemDetails({
  item,
  open,
  setOpen,
  latestAttribute,
  imperial,
}: IReadOnlyItemDetailsProps) {
  const alias = useMemo(
    () => item.alias || latestAttribute.alias || item.aliases?.[0]?.alias,
    [item, latestAttribute],
  );

  const isMultiple = useMemo(() => item?.aliases?.length > 1, [item]);

  return (
    <>
      <TableCell>
        <div className="d-flex align-items-center">
          <ItemImage item={item} imageKey="p_images" displayImageKey="display_image" />
          <div className="ml-3">
            <div>{item.p_title}</div>
            <div className="d-flex mt-1">
              <MuteText>SKU</MuteText>
              <BoldText className="ml-2">{item.p_sku}</BoldText>
            </div>
            <div className="d-flex mt-1">
              <MuteText>UPC</MuteText>
              <span className="ml-2">{item.item_upc}</span>
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell>
        {isMultiple ? (
          <ExpandCollapseToggle open={open} setOpen={setOpen} />
        ) : (
          <ProductPropertiesConverted
            attributes={{ ...latestAttribute, alias }}
            imperial={imperial}
          />
        )}
      </TableCell>
    </>
  );
}

interface IStatusRendererProps {
  qty: number;
  received_qty: number;
}

export function StatusRenderer({ qty, received_qty }: IStatusRendererProps) {
  if (qty === received_qty) {
    return (
      <ColoredPill color={theme.colors.green}>
        <Check />
        <span>Stored</span>
      </ColoredPill>
    );
  }

  return (
    <ColoredPill color={theme.colors.grey}>
      <span>Pending Storage</span>
    </ColoredPill>
  );
}
